import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Avatar, Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from "@mui/material";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useState } from "react";
import { useContactCenterTheme } from "../../virtual-receptionist/theming/ContactCenterThemeContext";
import { MessageGroupSummary } from "../messageGroups.types";
import { getAvatarColor, getGroupInitials } from "../utils/messageGroupDisplay.utils";

interface MessageGroupHeaderProps {
  group?: MessageGroupSummary | null;
  fallbackName: string;
  canManage: boolean;
  membersOpen?: boolean;
  onOpenMembers: () => void;
  onRename: () => void;
  onAddMembers: () => void;
  onLeave: () => void;
  onDelete: () => void;
}

export function MessageGroupHeader({
  group,
  fallbackName,
  canManage,
  membersOpen = false,
  onOpenMembers,
  onRename,
  onAddMembers,
  onLeave,
  onDelete,
}: MessageGroupHeaderProps) {
  const { theme } = useContactCenterTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const name = group?.name ?? fallbackName;
  const membersCount = group?.membersCount ?? 0;

  const closeMenu = () => setAnchorEl(null);
  const runAndClose = (action: () => void) => () => {
    closeMenu();
    action();
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        px: 1,
        py: 0.5,
        borderBottom: "1px solid",
        borderColor: theme.shell.border,
        backgroundColor: theme.shell.headerBg,
        color: theme.shell.textPrimary,
        flexShrink: 0,
        minHeight: 48,
        gap: 1,
      }}
    >
      <Avatar
        src={group?.avatar || undefined}
        alt={name}
        sx={{ width: 32, height: 32, fontSize: "0.75rem", bgcolor: group?.avatar ? undefined : getAvatarColor(name) }}
      >
        {group?.avatar ? null : getGroupInitials(name)}
      </Avatar>

      <Box
        onClick={onOpenMembers}
        sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", cursor: "pointer" }}
      >
        <Typography noWrap sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.2 }}>
          {name}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, mt: 0.25 }}>
          <GroupsRoundedIcon sx={{ fontSize: 12, color: theme.shell.textSecondary }} />
          <Typography noWrap sx={{ fontSize: 11, color: theme.shell.textSecondary, lineHeight: 1.2 }}>
            {membersCount} {membersCount === 1 ? "member" : "members"}
          </Typography>
          <ChevronRightIcon
            sx={{
              fontSize: 13,
              color: theme.shell.textSecondary,
              transform: membersOpen ? "rotate(90deg)" : "none",
              transition: "transform 0.2s ease",
            }}
          />
        </Box>
      </Box>

      <IconButton size="small" aria-label="Group options" onClick={(event) => setAnchorEl(event.currentTarget)} sx={{ color: theme.shell.textSecondary }}>
        <MoreVertIcon sx={{ fontSize: 20 }} />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        sx={{ zIndex: "2147483647 !important" }}
        slotProps={{ paper: { sx: { minWidth: 190, zIndex: "2147483647 !important" } } }}
      >
        {canManage && (
          <MenuItem onClick={runAndClose(onRename)}>
            <ListItemIcon>
              <DriveFileRenameOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: 13 }}>Rename group</ListItemText>
          </MenuItem>
        )}
        {canManage && (
          <MenuItem onClick={runAndClose(onAddMembers)}>
            <ListItemIcon>
              <PersonAddAlt1Icon fontSize="small" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: 13 }}>Add members</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={runAndClose(onLeave)}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: 13 }}>Leave group</ListItemText>
        </MenuItem>
        {canManage && (
          <MenuItem onClick={runAndClose(onDelete)} sx={{ color: "error.main" }}>
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: 13 }}>Delete group</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}
